<template>
  <div>
    <div class="is-flex is-justify-content-space-between is-align-items-center">
      <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
        <b-breadcrumb-item active>
          {{ $t('secrets.title') }}
        </b-breadcrumb-item>
      </b-breadcrumb>
      <b-button type="is-primary" @click="$router.push({ name: 'secret-add' })">{{ $t('secrets.addNew') }}</b-button>
    </div>
    <div class="list-table-container">
      <b-table :data="rows" striped :loading="loading" @click="handleRowClick"
        sort-icon="menu-up" default-sort="name" default-sort-direction="asc">
        <b-table-column field="name" sortable :label="$t('secrets.name')" v-slot="props">
          {{ props.row.name }}
        </b-table-column>
        <b-table-column field="usageCount" sortable :label="$t('secrets.usageCount')" v-slot="props">
          {{ props.row.usageCount || 0 }}
        </b-table-column>
        <b-table-column field="updatedAt" sortable :label="$t('secrets.lastUpdated')" v-slot="props">
          <span v-if="props.row.updatedAt">{{ $d(new Date(props.row.updatedAt), 'short') }}</span>
          <span v-else>—</span>
        </b-table-column>
        <b-table-column field="accessedAt" sortable :label="$t('secrets.lastAccessed')" v-slot="props">
          <span v-if="props.row.accessedAt">{{ $d(new Date(props.row.accessedAt), 'short') }}</span>
          <span v-else>—</span>
        </b-table-column>
        <b-table-column :label="$t('global.action')" v-slot="props">
          <b-button type="is-danger" size="is-small" icon-left="delete" :title="$t('global.delete')" aria-label="delete" @click.stop="confirmRemove(props.row)"></b-button>
        </b-table-column>
        <template #empty>
          <div class="has-text-centered mt-4 mb-4">{{ $t('secrets.noSecrets') }}</div>
        </template>
      </b-table>
    </div>
  </div>
 </template>

<script lang="ts">
import { defineComponent, computed, onMounted, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import KeyCloakService from '@/security/KeycloakService'

type SecretRow = { name: string; usageCount?: number; accessedAt?: string; createdAt?: string; updatedAt?: string }

export default defineComponent({
  name: 'SecretList',
  setup() {
    const router = useRouter()
    const store = useStore()
    const vm = getCurrentInstance()
    const rows = computed<SecretRow[]>(() => Object.values(store.state.secrets || {}))
    const loading = computed<boolean>(() => store.state.loading.secrets)

    const load = async () => {
      await store.dispatch('loadModel', { model: 'secrets' })
    }

    const remove = async (row: SecretRow) => {
      await store.dispatch('deleteSecret', { name: row.name })
    }

    const confirmRemove = (row: SecretRow) => {
      type DialogProxy = { $buefy?: { dialog?: { confirm?: (opts: { message: string; type?: string; confirmText?: string; cancelText?: string; hasIcon?: boolean; onConfirm?: () => void }) => void } } }
      type I18nProxy = { $t?: (key: string, vars?: Record<string, string>) => string }
      const proxy = vm?.proxy as unknown as DialogProxy & I18nProxy
      if (row.usageCount && row.usageCount > 0) {
        const msg = proxy?.$t ? proxy.$t('secrets.inUseMessage', { count: String(row.usageCount) }) : `Secret is in use by ${row.usageCount} integrations. Remove it there first.`
        const openDetails = proxy?.$t ? proxy.$t('secrets.openDetails') : 'Open details'
        const closeText = proxy?.$t ? proxy.$t('global.close') : 'Close'
        proxy?.$buefy?.dialog?.confirm?.({
          message: msg,
          type: 'is-info',
          confirmText: openDetails,
          cancelText: closeText,
          hasIcon: true,
          onConfirm: () => { router.push({ name: 'secret-details', params: { name: row.name } }) }
        })
        return
      }
      const msg = proxy?.$t ? proxy.$t('secrets.confirmDelete', { name: row.name }) : `Delete secret ${row.name}?`
      const confirmText = proxy?.$t ? proxy.$t('global.delete') : 'Delete'
      const cancelText = proxy?.$t ? proxy.$t('global.cancel') : 'Cancel'
      proxy?.$buefy?.dialog?.confirm?.({
        message: msg,
        type: 'is-danger',
        confirmText,
        cancelText,
        hasIcon: true,
        onConfirm: () => { remove(row) }
      })
    }

    const handleRowClick = (row: SecretRow) => {
      router.push({ name: 'secret-details', params: { name: row.name } })
    }

    onMounted(load)
    return { rows, loading, remove, confirmRemove, handleRowClick }
  }
})
</script>



